import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DatePipe } from '@angular/common';
import { SubSink } from 'subsink';
import { REQUIRED_MESSAGE, CARRIER_UPLOAD_REQUESTED } from '../../constants';
import { ROUTER_DQF_VIEW } from '../../routes';
import { Location } from '@angular/common';
import { CommonDataService } from '../../services/common/common-data.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { ImageTransform } from 'ngx-image-cropper';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { SelectActionComponent } from '../../dialog/select-action/select-action.component';
import { CropImageBottomSheetComponent, CropImageBottomSheetResult } from '../crop-image-bottom-sheet/crop-image-bottom-sheet.component';

const DELETE_MESSAGE = 'Are you sure you want to delete the Record?';
const UPLOAD_TYPE_ERROR = 'Upload pdf, jpeg, jpg or png files';
const PRCARD_DOCUMENT_TYPE = 'PRCard';
const NO_ISSUE_DATE_DOCUMENTS = [PRCARD_DOCUMENT_TYPE];
const PASSPORT_DOCUMENT_TYPE = 'Passport';
const CANADAIAN_VISA_DOCUMENT = 'CanadaVisa';
const US_VISA_DOCUMENT = 'USVisa';
const WORK_PERMIT_DOCUMENT = 'WorkPermit';
const FAST_CARD = 'FastCard';
const HEALTH_CARD = 'HealthCard';
export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY'
  },
  display: {
    dateInput: 'DD MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};
const EMERGENCY_RESPONSE_TRAINING = 'EmergencyResponseTraining';
const FATIGUE_MANAGEMENT__TRAINING = 'FatigueManagementTraining';
const HAZARDOUS_MATERIALS_ENDORSEMENT = 'HazardousMaterialsEndorsement';
const HOURS_OF_SERVICE = 'HoursOfService';
const MANDATORY_ENTRY_LEVEL_TRAINING = 'MandatoryEntryLevelTraining';
const MOUNTAIN_DRIVING = 'MountainDriving';
const TRANSPORTATION_OF_DANGEROUS_GOODS = 'TransportationOfDangerousGoods';
const VEHICLE_INSPECTIONS = 'VehicleInspections';
const WINTER_DRIVING = 'WinterDriving';
const WORKPLACE_HAZARDOUS_MATERIALS_INFORMATION_SYSTEM = 'WorkplaceHazardousMaterialsInformationSystem';
const DEFENSIVE_DRIVING = 'DefensiveDriving';
const CARGO_SECUREMENT = 'CargoSecurement';
const TRAINING_CERTIFICATE_DOCUMENTS = [
  EMERGENCY_RESPONSE_TRAINING,
  FATIGUE_MANAGEMENT__TRAINING,
  HAZARDOUS_MATERIALS_ENDORSEMENT,
  HOURS_OF_SERVICE,
  MANDATORY_ENTRY_LEVEL_TRAINING,
  MOUNTAIN_DRIVING,
  TRANSPORTATION_OF_DANGEROUS_GOODS,
  VEHICLE_INSPECTIONS,
  WINTER_DRIVING,
  WORKPLACE_HAZARDOUS_MATERIALS_INFORMATION_SYSTEM,
  DEFENSIVE_DRIVING,
  CARGO_SECUREMENT
];

const EXTRACTION_SUPPORTED_DOCUMENTS = [
  PASSPORT_DOCUMENT_TYPE,
  PRCARD_DOCUMENT_TYPE,
  CANADAIAN_VISA_DOCUMENT,
  US_VISA_DOCUMENT,
  WORK_PERMIT_DOCUMENT,
  FAST_CARD,
  HEALTH_CARD
];

@Component({
    selector: 'app-upload-document',
    templateUrl: './upload-document.component.html',
    styleUrls: ['./upload-document.component.scss'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        DatePipe
    ],
    standalone: false
})
export class UploadDocumentComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() documentDqfStatus;
  @Input() formUploadFiles;
  @Input() documentType?: string;
  @Input() hideTabs?: boolean;
  @Input() isExtracting?: boolean = false;
  @Input() isSaving?: boolean = false;
  @Input() extractionFailed?: boolean = false;
  @Input() extractionWarning?: string = null;
  @Input() canUndoCarrierRequest = false;
  @Output() uploadDocument = new EventEmitter<any>();
  @Output() saveDocument = new EventEmitter<any>();
  @Output() deleteDocument = new EventEmitter<any>();
  @Output() clearFile = new EventEmitter<void>();
  @Output() requestCarrier = new EventEmitter<any>();
  @Output() undoRequestCarrier = new EventEmitter<void>();
  currentDate = new Date();
  form: UntypedFormGroup;
  uploadedFile: any;
  required = REQUIRED_MESSAGE;
  acceptTypesPicture = ['image/png', 'image/jpg', 'image/jpeg'];
  acceptTypesMultiple = ['application/pdf'];
  uploadError = null;
  isUploading: boolean = false;
  subSink = new SubSink();
  isRequested: boolean = false;
  isExpiring: boolean = true;
  isCarrierRequested = true;
  documentPicture: any;
  previewUrl: string | null = null;
  transform: ImageTransform = {};
  expireMinDate = new Date(new Date().setDate(new Date().getDate() + 1));
  @ViewChild('generationDate') generationDate: MatDatepicker<Date>;
  isIssued: boolean;
  isFastCard: boolean;
  isHealthCard: boolean;
  expiryMandatoryDocument = [
    PRCARD_DOCUMENT_TYPE,
    PASSPORT_DOCUMENT_TYPE,
    CANADAIAN_VISA_DOCUMENT,
    US_VISA_DOCUMENT,
    WORK_PERMIT_DOCUMENT,
    FAST_CARD
  ];
  isTrainingDocument: any;
  isPRCard: boolean;
  documentTypeDisplayMap: { [key: string]: string } = {
    EmergencyResponseTraining: 'Emergency Response Training',
    FatigueManagementTraining: 'Fatigue Management Training',
    HazardousMaterialsEndorsement: 'Hazardous Materials Endorsement',
    HoursOfService: 'Hours of Service',
    MandatoryEntryLevelTraining: 'Mandatory Entry Level Training',
    MountainDriving: 'Mountain Driving',
    TransportationOfDangerousGoods: 'Transportation of Dangerous Goods',
    VehicleInspections: 'Vehicle Inspections',
    WinterDriving: 'Winter Driving',
    WorkplaceHazardousMaterialsInformationSystem: 'Workplace Hazardous Materials Information System',
    DefensiveDriving: 'Defensive Driving',
    CargoSecurement: 'Cargo Securement'
  };
  selectedTabIndex = 0;

  constructor(
    private router: Router,
    private readonly location: Location,
    private readonly commonDataService: CommonDataService,
    @Inject('environmentData') public environment: any,
    private readonly bottomSheet: MatBottomSheet,
    private readonly activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder
  ) {}
  ngOnInit(): void {
    // reading the config data and assigning the values, if we don't value
    // default value false will used
    this.commonDataService
      .getConfig()
      .then((response: any) => {
        if (response && response.status === 200) {
          return response.json();
        }
      })
      .then(
        (config: any) => {
          if (config) {
            this.commonDataService.isCERTNEnableForAbstract = config.isCERTNEnableForAbstract;
            this.commonDataService.isCERTNEnableForPoliceClearance = config.isCERTNEnableForPoliceClearance;
          }
        },
        error => {}
      );

    this.checkIfDocumentRequested();
    this.form = this.formUploadFiles.form;
    
    this.isExpiring = !!this.form.get('validToDate');
    this.isIssued = !!this.form.get('issueDate');
    this.isFastCard = !!this.form.get('fastCardNumber');
    this.isHealthCard = !!this.form.get('healthCardNumber');
    this.isTrainingDocument = TRAINING_CERTIFICATE_DOCUMENTS.includes(this.documentType);
    this.isPRCard = NO_ISSUE_DATE_DOCUMENTS.includes(this.documentType);
    
    if (this.isTrainingDocument) {
      const displayValue = this.documentTypeDisplayMap[this.documentType] || this.documentType;
      this.form.get('trainingCertificationName')?.patchValue(displayValue);
    }
    
    this.subSink.sink = this.form.get('file').valueChanges.subscribe(value => {
      if (value?.id) {
        this.uploadedFile = { ...value };
        this.isUploading = false;
      } else {
        this.uploadedFile = null;
      }
    });

    // Initialize from current form value when the file was patched before this component mounted.
    const currentFile = this.form.get('file')?.value;
    if (currentFile?.id) {
      this.uploadedFile = { ...currentFile };
      this.isUploading = false;
    }
    
    this.checkDataFormPage();
  }

  checkIfDocumentRequested(): void {
    this.isRequested = this.documentDqfStatus === CARRIER_UPLOAD_REQUESTED;

    if (this.documentDqfStatus === CARRIER_UPLOAD_REQUESTED) {
      this.isCarrierRequested = false;
      this.selectedTabIndex = 1;
    }
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  ngAfterViewInit(): void {}
  checkDataFormPage(): void {
    if (!this.formUploadFiles || !this.documentDqfStatus) {
      this.location.back();
    }
  }

  get validToDate() {
    return this.form.get('validToDate');
  }

  get issueDate() {
    return this.form.get('issueDate');
  }

  get fastCardNumber() {
    return this.form.get('fastCardNumber');
  }

  get healthCardNumber() {
    return this.form.get('healthCardNumber');
  }

  get trainingCertificationName() {
    return this.form.get('trainingCertificationName');
  }
  get issuingOrganization() {
    return this.form.get('issuingOrganization');
  }
  get credentialId() {
    return this.form.get('credentialId');
  }
  get credentialUrl() {
    return this.form.get('credentialUrl');
  }

  get isExtractionSupported(): boolean {
    return EXTRACTION_SUPPORTED_DOCUMENTS.includes(this.documentType);
  }

  onSaveDocument(): void {
    this.saveDocument.emit();
  }

  setExpireMinDate(event): void {
    const newMin = this.currentDate > event.value ? this.currentDate : event.value;
    this.expireMinDate = newMin;

    // Only clear validToDate if its current value is now before the new minimum
    const currentExpiry = this.validToDate?.value;
    if (currentExpiry && new Date(currentExpiry) <= newMin) {
      this.validToDate?.setValue('');
    }
  }

  openActionSheet(pictureSingle, uploadMultiple): void {
    
    if (this.environment?.featureFlags?.enableDQFDocImageCropFeature) {
      let options: any = [
        {
          displayName: 'Take picture (Single page)',
          name: 'picture'
        },
        {
          displayName: 'Upload File (Multiple pages)',
          name: 'upload'
        }
      ];
      this.bottomSheet.open(SelectActionComponent, {
        data: {
          title: 'Upload Document',
          options: options,
          selectedValue: null,
          onConfirm: (val: string) => {
            if (val === 'picture') {
              this.onFileInputClicked(pictureSingle);
            } else {
              this.onFileInputClicked(uploadMultiple);
            }
          }
        }
      });
    } else {
      this.onFileInputClicked(uploadMultiple);
    }
  }

  onUploadFileChange($event: any): void {
    this.isUploading = true;
    this.uploadError = null;
    if (!$event || $event.target.files.length === 0) {
      return;
    }
    const [file] = $event.currentTarget.files;
    if (!this.acceptTypesMultiple.includes(file.type)) {
      this.uploadError = UPLOAD_TYPE_ERROR;
      this.isUploading = false;
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.previewUrl = file.type === 'application/pdf' ? null : (reader.result as string);
      const fileWithMetadata = Object.assign(file, {
        id: 'temp-' + Date.now(),
        fileName: file.name
      });
      this.form.get('file').patchValue(fileWithMetadata);
      $event.target.files = null;
      $event.target.value = null;
      this.uploadDocument.emit();
    };
  }

  viewDocument(file): void {
    this.router.navigateByUrl(`${ROUTER_DQF_VIEW}/${file.id}`);
  }

  deleteDocuments(file): void {
    this.deleteDocument.emit();
  }

  clearUploadedFile(): void {
    this.form.get('file')?.setValue('');
    this.uploadedFile = null;
    this.uploadError = null;
    this.previewUrl = null;
    this.clearFile.emit();
  }
  onRequestCarrierUpload(): void {
    this.requestCarrier.emit();
  }

  onUndoRequestCarrier(): void {
    this.undoRequestCarrier.emit();
  }

  isRequestCollect($event): void {
    // Match Pre-Employment: switching to Request Carrier only selects the tab;
    // the driver must click "Request Carrier Upload" to send the request.
    this.isCarrierRequested = true;
  }

  onFileInputClicked(fileInput: any) {
    fileInput.click();
  }

  onProcessPictureFile(event: any): void {
    if (!event.target.files?.length) {
      return;
    }
    const [file] = event.target.files;
    this.documentPicture = file;

    const sheetRef = this.bottomSheet.open(CropImageBottomSheetComponent, {
      data: { imageChangedEvent: event, originalFile: file },
      panelClass: 'crop-image-bottom-sheet-panel'
    });

    sheetRef.afterDismissed().subscribe((result: CropImageBottomSheetResult | null) => {
      this.documentPicture = null;
      event.target.value = null;

      if (!result) {
        return;
      }

      // Generate preview URL from the cropped image file
      const previewReader = new FileReader();
      previewReader.readAsDataURL(result.croppedFile);
      previewReader.onload = () => {
        this.previewUrl = previewReader.result as string;
      };

      const fileWithMetadata = Object.assign(result.croppedFile, {
        id: 'temp-' + Date.now(),
        fileName: result.croppedFile.name
      });
      this.form.get('file').patchValue(fileWithMetadata);
      this.uploadDocument.emit();
    });
  }

  onDatePickerClick(openFn: () => void): void {
    if (openFn) {
      openFn();
    }
  }

  clearDateField(controlName: string): void {
    this.form.get(controlName)?.setValue('');
  }





  // Method to handle Add File button click
  onAddFileClick(pictureSingle, uploadMultiple): void {
    this.openActionSheet(pictureSingle, uploadMultiple);
  }


}
